export function normalizeAdminPath(pathname = "") {
  return pathname.replace(/^\/[^/]+(?=\/administrator(?:\/|$))/, "");
}

// Matches the calibration-admin landing (/admin) and its sub-pages
// (audit-log, roles, cron-health, reports), including the
// tenant-prefixed variants (/<clan>/admin...). Deliberately anchored on
// the "/admin" segment so it never collides with the longer
// "/administrator" routes that share the same prefix.
//
// Calibration fold-in, Stage 3 (.agent/clan-admin-parity-execplan.md §2
// point 3): the same four pages are now ALSO nested inside AdminLayout's
// own Outlet at /administrator/calibration(/roles|/reports|/audit-log
// |/cron-health) — see router.jsx's calibrationFoldInRoutes. AdminLayout's
// own Calibration nav item now points there, so this match is extended
// (not replaced) to cover both locations: the legacy standalone /admin
// family is retained as-is (Stage 4, not this stage, retires those
// routes, and AdminLayout.routes.test.jsx still exercises it directly),
// and the new /administrator/calibration family is added alongside it.
export function isCalibrationAdminRoute(pathname = "") {
  if (
    /(?:^|\/[^/]+)\/admin(?:\/(?:audit-log|roles|cron-health|reports))?\/?$/.test(
      pathname
    )
  ) {
    return true;
  }

  const normalizedPath = normalizeAdminPath(pathname);
  return (
    normalizedPath === "/administrator/calibration" ||
    normalizedPath.startsWith("/administrator/calibration/")
  );
}

export function isTenantAdminRoute(pathname) {
  const normalizedPath = normalizeAdminPath(pathname);
  return (
    normalizedPath === "/administrator" ||
    normalizedPath === "/administrator/users" ||
    normalizedPath.startsWith("/administrator/users/") ||
    normalizedPath === "/administrator/subclans" ||
    normalizedPath.startsWith("/administrator/subclans/") ||
    normalizedPath === "/administrator/events" ||
    normalizedPath.startsWith("/administrator/events/") ||
    normalizedPath === "/administrator/content" ||
    normalizedPath.startsWith("/administrator/content/") ||
    normalizedPath === "/administrator/moderation" ||
    normalizedPath.startsWith("/administrator/moderation/") ||
    // Calibration fold-in, Stage 3 (.agent/clan-admin-parity-execplan.md §2
    // point 3 / router.jsx's calibrationFoldInRoutes). Without this pair, a
    // tenant owner/tenant admin (NOT a platform admin, who bypasses this
    // check entirely) landing on any of the four folded-in calibration
    // pages gets bounced straight back to /administrator by AdminLayout's
    // own access-redirect effect — the exact regression this stage exists
    // to prevent. See AdminLayout.routes.test.jsx and
    // AdminLayout.savebar.test.jsx for the tests proving this.
    normalizedPath === "/administrator/calibration" ||
    normalizedPath.startsWith("/administrator/calibration/") ||
    normalizedPath === "/administrator/settings" ||
    normalizedPath.startsWith("/administrator/settings/") ||
    normalizedPath === "/administrator/menu-builder" ||
    normalizedPath.startsWith("/administrator/menu-builder/") ||
    normalizedPath === "/administrator/pages" ||
    normalizedPath.startsWith("/administrator/pages/") ||
    // Domains stage (.agent/clan-admin-parity-execplan.md): this pair
    // already covered /administrator/domains before that route was
    // promoted from a <Navigate> redirect to a real, flat page
    // (pages/admin/index.jsx) — no change needed here, this allowlist
    // entry was never conditioned on the route being a redirect.
    normalizedPath === "/administrator/domains" ||
    normalizedPath.startsWith("/administrator/domains/") ||
    normalizedPath === "/administrator/notifications" ||
    normalizedPath.startsWith("/administrator/notifications/") ||
    normalizedPath === "/administrator/audit-trail" ||
    normalizedPath.startsWith("/administrator/audit-trail/")
  );
}

export function isAdminNavItemActive(item, pathname) {
  if (!item?.to || item.external) {
    return false;
  }

  if (typeof item.activeMatch === "function") {
    return item.activeMatch(pathname);
  }

  const normalizedPath = normalizeAdminPath(pathname);
  const activePaths =
    Array.isArray(item.activePaths) && item.activePaths.length
      ? item.activePaths
      : [item.to];

  return activePaths.some((path) => {
    if (item.exact || path === "/administrator") {
      return normalizedPath === path;
    }

    return normalizedPath === path || normalizedPath.startsWith(`${path}/`);
  });
}
